import { PageHeader, LoadingState, DataTable, ActionToolbar, EmptyState } from '../components/AdminPageUI';
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Grid, TextField, Button, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, IconButton, Card, CardContent,
  Container, Avatar, Stack, Tooltip, Chip,
  useMediaQuery
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarToday,
  Event,
  Schedule,
  DateRange
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../utils/Api';

const CalendarPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fetching, setFetching] = useState(true);

  const [form, setForm] = useState({
    academicYear: '',
    term: '',
    startDate: '',
    endDate: '',
    weekNumber: '',
    weekStartDate: ''
  });

  const [errors, setErrors] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  // Function to extract school ID from user object
  const getSchoolId = useCallback(() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;

      const user = JSON.parse(raw);
      const school = user?.school;

      if (!school) return null;

      // Case 1: school is a plain string ID
      if (typeof school === 'string') {
        return school;
      }

      // Case 2: school is a populated object
      if (school._id) {
        return school._id.toString();
      }

      // Case 3: fallback for weird formats (rare)
      if (school.id) {
        return school.id.toString();
      }

      // Final fallback: try to stringify whatever it is
      return String(school);
    } catch (error) {
      // console.error("Error extracting school ID:", error);
      return null;
    }
  }, []);

  const fetchTerms = useCallback(async () => {
    try {
      setLoading(true);
      const schoolId = getSchoolId();
      if (!schoolId) {
        throw new Error("School ID not available");
      }

      // console.log("Fetching terms for school ID:", schoolId);
      const response = await api.get(`/term/all?school=${schoolId}`);
      setTerms(response.data.allTerms || []);
    } catch (error) {
      // console.error("❌ Error fetching terms:", error);
      toast.error('Failed to fetch terms: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
      setFetching(false);
    }
  }, [getSchoolId]);

  useEffect(() => {
    fetchTerms();
  }, [fetchTerms]);

  const validateForm = () => {
    const newErrors = {
      academicYear: !form.academicYear,
      term: !form.term,
      startDate: !form.startDate,
      endDate: !form.endDate,
      weekNumber: !form.weekNumber,
      weekStartDate: !form.weekStartDate
    };

    if (form.endDate && form.startDate && new Date(form.endDate) <= new Date(form.startDate)) {
      newErrors.endDate = true;
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const updatedForm = { ...prev, [name]: value };

      if (name === 'startDate') {
        updatedForm.weekStartDate = value;
      }

      return updatedForm;
    });
    setErrors(prev => ({ ...prev, [name]: false }));
  };

  const generateWeeks = useCallback((startDate, endDate) => {
    const weeks = [];
    let weekNumber = 1;
    let currentDate = new Date(startDate);
    const termEndDate = new Date(endDate);

    while (currentDate <= termEndDate) {
      const weekStartDate = new Date(currentDate);
      let weekEndDate = new Date(currentDate);
      weekEndDate.setDate(weekEndDate.getDate() + 6);

      if (weekEndDate > termEndDate) {
        weekEndDate = new Date(termEndDate);
      }

      weeks.push({
        weekNumber: weekNumber++,
        weekStartDate: weekStartDate.toISOString(),
        weekEndDate: weekEndDate.toISOString(),
        label: `Week ${weekNumber - 1}`
      });

      currentDate.setDate(currentDate.getDate() + 7);
    }

    return weeks;
  }, []);

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix validation errors');
      return;
    }

    try {
      setSubmitting(true);
      const schoolId = getSchoolId();
      if (!schoolId) {
        toast.error('School information not available');
        return;
      }

      const formattedAcademicYear = form.academicYear.replace(/\//g, '-');
      const weeksArray = generateWeeks(form.startDate, form.endDate);
      const totalWeeks = weeksArray.length;

      const payload = {
        academicYear: formattedAcademicYear,
        term: form.term,
        startDate: form.startDate,
        endDate: form.endDate,
        school: schoolId,
        weekNumber: Number(form.weekNumber),
        weekStartDate: form.weekStartDate,
        weeks: totalWeeks,
        weeksData: weeksArray
      };

      // console.log("Saving term payload:", payload);

      let response;
      if (editMode && editId) {
        response = await api.put(`/term/${editId}`, payload);
        toast.success(response.data.message || 'Term updated successfully');
      } else {
        response = await api.post('/term/add', payload);
        toast.success(response.data.message || 'Term created successfully');
      }

      await fetchTerms();
      resetForm();
    } catch (error) {
      // console.error('❌ Error saving term:', error);
      toast.error(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setEditMode(false);
    setEditId(null);
    setForm({
      academicYear: '',
      term: '',
      startDate: '',
      endDate: '',
      weekNumber: '',
      weekStartDate: ''
    });
    setErrors({});
  };

  const handleEdit = useCallback((term) => {
    const frontendAcademicYear = term.academicYear.replace(/-/g, '/');

    setForm({
      academicYear: frontendAcademicYear,
      term: term.term,
      startDate: term.startDate?.substring(0, 10),
      endDate: term.endDate?.substring(0, 10),
      weekNumber: term.weekNumber || '',
      weekStartDate: term.weekStartDate?.substring(0, 10) || term.startDate?.substring(0, 10)
    });
    setEditMode(true);
    setEditId(term._id);
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm("Are you sure you want to delete this term?")) return;
    try {
      const response = await api.delete(`/term/${id}`);
      await fetchTerms();
      toast.success(response.data.message || 'Term deleted successfully');
    } catch (error) {
      // console.error('❌ Error deleting term:', error);
      toast.error(`Error: ${error.response?.data?.message || error.message}`);
    }
  }, [fetchTerms]);

  const formatAcademicYearDisplay = (year) => {
    return year.replace(/-/g, '/');
  };

  const getTermColor = (term) => {
    const termColors = {
      'Term 1': 'primary',
      'Term 2': 'secondary',
      'Term 3': 'success'
    };
    return termColors[term] || 'default';
  };

  // Mobile Term Card Component
  const MobileTermCard = ({ term }) => (
    <Card sx={{ mb: 2, boxShadow: 2, border: `1px solid ${theme.palette.divider}` }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* Header with Academic Year and Actions */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar sx={{
                bgcolor: theme.palette.primary.main,
                width: 40,
                height: 40
              }}>
                <CalendarToday fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="600">
                  {formatAcademicYearDisplay(term.academicYear)}
                </Typography>
                <Chip
                  label={term.term}
                  color={getTermColor(term.term)}
                  variant="filled"
                  size="small"
                  sx={{ fontWeight: 600, mt: 0.5 }}
                />
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Edit Term">
                <IconButton
                  onClick={() => handleEdit(term)}
                  color="primary"
                  size="small"
                  sx={{
                    minWidth: 32,
                    minHeight: 32,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Term">
                <IconButton
                  onClick={() => handleDelete(term._id)}
                  color="error"
                  size="small"
                  sx={{
                    minWidth: 32,
                    minHeight: 32,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Duration */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              DURATION
            </Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DateRange fontSize="small" color="action" />
                Start: {term.startDate?.substring(0, 10)}
              </Typography>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DateRange fontSize="small" color="action" />
                End: {term.endDate?.substring(0, 10)}
              </Typography>
            </Stack>
          </Box>

          {/* Week Info */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                WEEK NUMBER
              </Typography>
              <Chip
                label={term.weekNumber}
                color="secondary"
                variant="outlined"
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                TOTAL WEEKS
              </Typography>
              <Chip
                icon={<Schedule fontSize="small" />}
                label={term.weeks || 0}
                color="info"
                variant="outlined"
                size="small"
              />
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      {/* Header */}
      <PageHeader title={<>Academic Calendar</>} description={<>Manage academic terms and schedules</>}></PageHeader>

      {fetching ? (
        <LoadingState />
      ) : (
        <>
          {/* Term Form Card */}
          <Card sx={{
            mb: isMobile ? 3 : 4,
            boxShadow: isMobile ? 1 : 3,
            border: `1px solid ${theme.palette.divider}`
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 4 }}>
              <Stack
                direction={isMobile ? "column" : "row"}
                alignItems={isMobile ? "flex-start" : "center"}
                spacing={2}
                sx={{ mb: 3 }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{
                    bgcolor: theme.palette.primary.main,
                    width: isMobile ? 32 : 40,
                    height: isMobile ? 32 : 40
                  }}>
                    {editMode ? <EditIcon fontSize={isMobile ? "small" : "medium"} /> : <Event fontSize={isMobile ? "small" : "medium"} />}
                  </Avatar>
                  <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600">
                    {editMode ? 'Edit Term' : 'Add New Term'}
                  </Typography>
                </Stack>
              </Stack>

              {/* UPDATED TO GRID V2 - REMOVED item PROP AND UPDATED breakpoints */}
              <Grid container spacing={isMobile ? 2 : 3}>
                {/* Academic Year Dropdown */}
                <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                  <TextField
                    select
                    fullWidth
                    label="Academic Year"
                    name="academicYear"
                    value={form.academicYear}
                    onChange={handleChange}
                    error={errors.academicYear}
                    helperText={!form.academicYear ? "Select an academic year" : (errors.academicYear && 'Academic year is required')}
                    disabled={submitting}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: form.academicYear !== '' ? undefined : () => 'Select Academic Year'
                    }}
                    size={isMobile ? "small" : "medium"}
                  >
                    <MenuItem value="" disabled>
                      Select Academic Year
                    </MenuItem>
                    <MenuItem value="2024/2025">2024/2025</MenuItem>
                    <MenuItem value="2025/2026">2025/2026</MenuItem>
                    <MenuItem value="2026/2027">2026/2027</MenuItem>
                    <MenuItem value="2027/2028">2027/2028</MenuItem>
                    <MenuItem value="2028/2029">2028/2029</MenuItem>
                    <MenuItem value="2029/2030">2029/2030</MenuItem>
                  </TextField>
                </Grid>

                {/* Term Dropdown */}
                <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                  <TextField
                    select
                    fullWidth
                    label="Academic Term"
                    name="term"
                    value={form.term}
                    onChange={handleChange}
                    error={errors.term}
                    helperText={!form.term ? "Select an academic term" : (errors.term && 'Academic term is required')}
                    disabled={submitting}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: form.term !== '' ? undefined : () => 'Select Academic Term'
                    }}
                    size={isMobile ? "small" : "medium"}
                  >
                    <MenuItem value="" disabled>
                      Select Academic Term
                    </MenuItem>
                    <MenuItem value="Term 1">Term 1</MenuItem>
                    <MenuItem value="Term 2">Term 2</MenuItem>
                    <MenuItem value="Term 3">Term 3</MenuItem>
                  </TextField>
                </Grid>

                {/* Start Date */}
                <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    name="startDate"
                    value={form.startDate}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    error={errors.startDate}
                    helperText={errors.startDate && 'Start date is required'}
                    disabled={submitting}
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>

                {/* End Date */}
                <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                  <TextField
                    fullWidth
                    label="End Date"
                    name="endDate"
                    type="date"
                    value={form.endDate}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    error={errors.endDate}
                    helperText={errors.endDate ? "End date must be after start date" : "End date is required"}
                    disabled={submitting}
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>

                {/* Week Number */}
                <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                  <TextField
                    fullWidth
                    label="Week Number"
                    name="weekNumber"
                    type="number"
                    value={form.weekNumber}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    error={errors.weekNumber}
                    helperText={!form.weekNumber ? "Enter the week number" : (errors.weekNumber && 'Week number is required')}
                    disabled={submitting}
                    inputProps={{ min: 1 }}
                    placeholder="Enter week number"
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>

                {/* Week Start Date */}
                <Grid size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                  <TextField
                    fullWidth
                    label="Week Start Date"
                    name="weekStartDate"
                    type="date"
                    value={form.weekStartDate}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    error={errors.weekStartDate}
                    helperText={errors.weekStartDate && 'Week start date is required'}
                    disabled={submitting}
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <ActionToolbar
                    direction={isMobile ? "column" : "row"}
                    spacing={2}
                    justifyContent="flex-end"
                    alignItems={isMobile ? "stretch" : "center"}
                  >
                    {editMode && (
                      <Button
                        onClick={resetForm}
                        variant="outlined"
                        disabled={submitting}
                        fullWidth={isMobile}
                        size={isMobile ? "medium" : "large"}
                      >
                        Cancel Edit
                      </Button>
                    )}
                    <Button
                      onClick={handleSubmit}
                      variant="contained"
                      size={isMobile ? "medium" : "large"}
                      disabled={submitting}
                      fullWidth={isMobile}
                    >
                      {submitting ? <CircularProgress size={24} /> : editMode ? 'Update Term' : 'Create Term'}
                    </Button>
                  </ActionToolbar>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Terms List */}
          <Card sx={{
            boxShadow: isMobile ? 1 : 3,
            border: `1px solid ${theme.palette.divider}`
          }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{
                p: isMobile ? 2 : 3,
                borderBottom: `1px solid ${theme.palette.divider}`
              }}>
                <Typography variant={isMobile ? "h6" : "h6"} fontWeight="600">
                  Academic Terms ({terms.length})
                </Typography>
              </Box>

              {loading ? (
                <LoadingState />
              ) : isMobile ? (
                // Mobile View - Cards
                <Box sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    {terms.map((term) => (
                      <MobileTermCard key={term._id} term={term} />
                    ))}
                  </Stack>
                </Box>
              ) : (
                // Desktop/Tablet View - Table
                <Box sx={{
                  width: '100%',
                  overflow: 'auto',
                  maxHeight: isTablet ? '60vh' : 'none'
                }}>
                  <DataTable><Table sx={{ minWidth: isTablet ? 800 : 'auto' }}>
                    <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }}>ACADEMIC YEAR</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }}>TERM</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }}>DURATION</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }}>WEEK NUMBER</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2 }}>TOTAL WEEKS</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', py: 2, textAlign: 'center' }}>ACTIONS</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
{terms.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                      {terms.map((term) => (
                        <TableRow
                          key={term._id}
                          sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                        >
                          <TableCell sx={{ py: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Avatar sx={{
                                bgcolor: theme.palette.primary.main,
                                width: 40,
                                height: 40
                              }}>
                                <CalendarToday />
                              </Avatar>
                              <Typography variant="subtitle1" fontWeight="500">
                                {formatAcademicYearDisplay(term.academicYear)}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              label={term.term}
                              color={getTermColor(term.term)}
                              variant="filled"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Stack spacing={0.5}>
                              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DateRange fontSize="small" />
                                Start: {term.startDate?.substring(0, 10)}
                              </Typography>
                              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DateRange fontSize="small" />
                                End: {term.endDate?.substring(0, 10)}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              label={term.weekNumber}
                              color="secondary"
                              variant="outlined"
                              sx={{ fontWeight: 'bold', minWidth: 50 }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              icon={<Schedule />}
                              label={term.weeks || 0}
                              color="info"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2, textAlign: 'center' }}>
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <Tooltip title="Edit Term">
                                <IconButton
                                  onClick={() => handleEdit(term)}
                                  color="primary"
                                  size="small"
                                  sx={{
                                    minWidth: 32,
                                    minHeight: 32,
                                    borderRadius: 1,
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Term">
                                <IconButton
                                  onClick={() => handleDelete(term._id)}
                                  color="error"
                                  size="small"
                                  sx={{
                                    minWidth: 32,
                                    minHeight: 32,
                                    borderRadius: 1,
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table></DataTable>
                </Box>
              )}

              {!loading && terms.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                  <Event sx={{
                    fontSize: isMobile ? 48 : 64,
                    color: 'text.secondary',
                    opacity: 0.5,
                    mb: 2
                  }} />
                  <Typography variant={isMobile ? "h6" : "h6"} color="text.secondary" gutterBottom>
                    No academic terms found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add your first term using the form above
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Container>
  );
};

export default CalendarPage;